import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader, ChevronRight } from 'lucide-react';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const CATEGORY_COLORS = [
  'rgba(247, 213, 110, 0.32)', // Soft yellow
  'rgba(247, 165, 165, 0.28)', // Soft pink
  'rgba(134, 221, 199, 0.30)', // Soft mint
  'rgba(196, 174, 227, 0.30)', // Soft lavender
  'rgba(247, 193, 145, 0.30)', // Soft peach
  'rgba(168, 196, 224, 0.32)', // Soft blue
  'rgba(177, 233, 203, 0.30)', // Soft green
  'rgba(180, 224, 239, 0.32)', // Soft sky
];

const getCategoryColor = (name) => {
  const index = name.charCodeAt(0) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[index];
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const navigateToCategoryBusinesses = (categoryId, categoryName) => {
    if (!categoryId) return;

    const params = new URLSearchParams({
      category: categoryId,
      categoryName: categoryName || 'Selected Category',
    });

    navigate(`/businesses?${params.toString()}`);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/categories/tree/full`);
      if (response.data?.status === 'success') {
        const activeCategories = response.data.data.filter((cat) => cat.is_active !== false);
        setCategories(activeCategories);
        setFilteredCategories(activeCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setFilteredCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowercaseQuery = query.toLowerCase();

    if (!query.trim()) {
      setFilteredCategories(categories);
    } else {
      const filtered = categories
        .map((category) => {
          const matchesCategory = category.name.toLowerCase().includes(lowercaseQuery);
          const matchingSubcategories = category.subcategories
            ? category.subcategories.filter((sub) => sub.name.toLowerCase().includes(lowercaseQuery))
            : [];

          if (matchesCategory || matchingSubcategories.length > 0) {
            return {
              ...category,
              subcategories: matchingSubcategories.length > 0 ? matchingSubcategories : category.subcategories,
            };
          }
          return null;
        })
        .filter(Boolean);

      setFilteredCategories(filtered);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Browse All Business Categories | Find Verified Services Near You | VMC"
        description="Explore businesses by category on VMC. Find trusted local services, compare verified customer reviews, and choose with confidence. Start your search today."
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container-custom pt-10 pb-12">
          <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Categories' }]} />

          <div className="mt-8">
            <h1 className="text-4xl font-bold tracking-tight mb-3">Find the Right Pro in Any Category</h1>
            <p className="text-slate-300 text-lg">
              From home services to professional consultants, browse thousands of verified business listings across all industries. Each category features businesses with authentic reviews and transparent trust signals.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 mb-6 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search categories and subcategories..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:bg-white/15 transition text-base"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Categories Masonry */}
      <div className="container-custom py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader className="animate-spin text-slate-400" size={32} />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-slate-600 text-lg">No categories found matching your search.</p>
          </div>
        ) : (
          <div className="columns-1 gap-4 sm:columns-2 md:columns-3 lg:columns-4">
            {filteredCategories.map((category) => {
              const gradient = getCategoryColor(category.name);
              const subcategoryCount = category.subcategories?.length || 0;

              return (
                <div
                  key={category.id}
                  className="group mb-4 inline-block w-full break-inside-avoid cursor-pointer rounded-2xl border border-slate-200 overflow-hidden transition-all duration-200 hover:-translate-y-0.5 bg-white"
                  onClick={() => navigateToCategoryBusinesses(category.id, category.name)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateToCategoryBusinesses(category.id, category.name);
                    }
                  }}
                >
                  {/* Color Header */}
                  <div style={{ backgroundColor: gradient }} className="p-6 text-slate-800 border-b border-slate-300/60">
                    <h3 className="font-bold text-lg leading-tight mb-3 text-slate-800">{category.name}</h3>
                    <div className="text-sm font-semibold text-slate-700">
                      {subcategoryCount} {subcategoryCount === 1 ? 'subcategory' : 'subcategories'}
                    </div>
                  </div>

                  {/* Subcategories List */}
                  {subcategoryCount > 0 && (
                    <div className="p-4 border-t border-slate-300/50">
                      <ul className="space-y-2">
                        {category.subcategories.map((sub) => (
                          <li
                            key={sub.id}
                            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 hover:bg-white/35 px-2 py-1 rounded transition-colors duration-150 group cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToCategoryBusinesses(sub.id, sub.name);
                            }}
                          >
                            <ChevronRight size={14} className="text-slate-600 group-hover:text-slate-800 flex-shrink-0" />
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-700 group-hover:text-slate-900">{sub.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Section */}
      <section className="container-custom py-12 bg-white rounded-3xl my-12 border border-slate-200">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Discover Trusted Businesses</h2>
          <p className="text-slate-600 mb-6">
            Each category contains verified businesses with customer reviews. Click on any category to explore available
            services and read genuine feedback from real customers.
          </p>
          <Link
            to="/businesses"
            className="inline-flex rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-700"
          >
            Browse All Businesses
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CategoriesPage;
